import * as React from "react";
import {
    Box,
    Button,
    Flashbar,
    Link,
    Modal,
    Select,
    SpaceBetween
} from "@amzn/awsui-components-react";
import { AppContext } from "../../setup/context";
import { AppURLFactory } from "../../factory/app-url-factory";
import {
    AdvancedListContent,
    APIOutput,
    Container,
    ContainerRef,
    DecisionTreeContent,
    EntityRef,
    EntityType,
    IAdvancedList,
    IContainer,
    IFile,
    ILegalContact,
    IPageDraft,
    LegalContact,
    Page,
    PageDraft,
    PageLibrary,
    S3File,
    ServerSidePaginatedLoadingInput,
    ServerSidePaginatedLoadingOutput
} from "@amzn/ask-legal-domain";
import { UIField } from "../common/UIField";
import { SDSUtils } from "../../utils/sds-utils";

export const CopyContainerModal = (props: {
    visible: boolean;
    onDismiss: () => void;
    sourceContainerId: string;
    sourceSection: Page.SectionLocation;
    instanceId: string;
}) => {
    const context = React.useContext(AppContext);

    const [pageLibraries, setPageLibraries] = React.useState<{ label: string; value: string }[]>([]);
    const [pageLibrariesLoaded, setPageLibrariesLoaded] = React.useState(false);
    const [selectedPL, setSelectedPL] = React.useState<string | null>(null);

    const [pages, setPages] = React.useState<{ label: string; value: string }[]>([]);
    const [pagesLoaded, setPagesLoaded] = React.useState(false);
    const [selectedPage, setSelectedPage] = React.useState<string | null>(null);

    const [submitting, setSubmitting] = React.useState(false);
    const [succeeded, setSucceeded] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [redirectUrl, setRedirectUrl] = React.useState<string | null>(null);
    const [redirectCountdown, setRedirectCountdown] = React.useState(5);

    // Load page libraries for this instance
    React.useEffect(() => {
        if (!props.visible) return;
        setPageLibrariesLoaded(false);
        context.getPageLibraryAPI().loadAll(
            ServerSidePaginatedLoadingInput.create({
                partitionKey: props.instanceId,
                currentPageIndex: 1,
                pageSize: 500,
                filters: []
            })
        ).then(res => {
            const output = APIOutput.fromRaw<{ result: PageLibrary[]; totalCount: number }>(res.data);
            if (output.isOk()) {
                setPageLibraries(output.data.result
                    .filter(pl => !pl.deprecated)
                    .map(pl => ({ label: pl.name || pl.id, value: pl.id }))
                    .sort((a, b) => a.label.localeCompare(b.label))
                );
            }
            setPageLibrariesLoaded(true);
        }).catch(() => setPageLibrariesLoaded(true));
    }, [props.visible, props.instanceId]);

    // Load pages when page library is selected
    React.useEffect(() => {
        if (!selectedPL) {
            setPages([]);
            setSelectedPage(null);
            return;
        }
        setPagesLoaded(false);
        setSelectedPage(null);
        context.getPageAPI().loadAll(
            ServerSidePaginatedLoadingInput.create({
                partitionKey: selectedPL,
                currentPageIndex: 1,
                pageSize: 500,
                filters: []
            })
        ).then(res => {
            const output = APIOutput.fromRaw<{ result: Page.Data[]; totalCount: number }>(res.data);
            if (output.isOk()) {
                setPages(output.data.result
                    .filter(p => !p.deprecated)
                    .map(p => ({ label: p.title || p.name || p.id, value: p.id }))
                    .sort((a, b) => a.label.localeCompare(b.label))
                );
            }
            setPagesLoaded(true);
        }).catch(() => setPagesLoaded(true));
    }, [selectedPL]);

    // Redirect countdown after success
    React.useEffect(() => {
        if (!succeeded || !redirectUrl || error) return;
        const countdown = setInterval(() => {
            setRedirectCountdown(prev => prev > 0 ? prev - 1 : 0);
        }, 1000);
        const redirect = setTimeout(() => {
            window.location.assign(redirectUrl);
        }, 5000);
        return () => {
            clearInterval(countdown);
            clearTimeout(redirect);
        };
    }, [succeeded, redirectUrl, error]);

    const copyTypeSpecificResources = async (
        containerType: Container.Type,
        sourceContainerId: string,
        newContainerId: string,
        loadedContent: any,
        draftRef: EntityRef
    ) => {
        switch (containerType) {
            case Container.Type.DOCUMENT: {
                // Copy each document file via presigned download/upload
                const allFiles = [
                    ...(loadedContent.files || []),
                    ...(loadedContent.fileGroups || []).flatMap((g: any) => g.files || [])
                ];
                const successfullyCopied = new Set<string>();
                for (const file of allFiles) {
                    const sourceKey = file.s3File?.key;
                    if (!sourceKey) continue;
                    try {
                        // Download from source
                        const getUrlRes = await context.getFileAPI().getPresignedUrl(
                            IFile.PresignedUrlInput.create({ key: sourceKey, operation: IFile.PresignedOperation.Get })
                        );
                        const getUrlOutput = APIOutput.fromRaw<IFile.PresignedUrlOutput>(getUrlRes.data);
                        if (getUrlOutput.isErr()) continue;
                        const downloadRes = await fetch(getUrlOutput.data.presignedUrl);
                        if (!downloadRes.ok) continue;
                        const blob = await downloadRes.blob();
                        // Upload to new container
                        const newKey = S3File.AskLegalFileKey.toAskLegalFileKeyFromDocumentFilename(newContainerId, file.filename);
                        const putUrlRes = await context.getFileAPI().getPresignedUrl(
                            IFile.PresignedUrlInput.create({ key: newKey, operation: IFile.PresignedOperation.Put })
                        );
                        const putUrlOutput = APIOutput.fromRaw<IFile.PresignedUrlOutput>(putUrlRes.data);
                        if (putUrlOutput.isErr()) continue;
                        await context.getFileAPI().uploadViaPresignedUrl({ data: blob, presignedUrl: putUrlOutput.data.presignedUrl });
                        successfullyCopied.add(file.filename);
                    } catch {
                        // Non-blocking — continue copying remaining files
                    }
                }
                // Remap s3File keys only for successfully copied files
                const remapFile = (file: any) => ({
                    ...file,
                    s3File: {
                        ...file.s3File,
                        key: successfullyCopied.has(file.filename)
                            ? S3File.AskLegalFileKey.toAskLegalFileKeyFromDocumentFilename(newContainerId, file.filename)
                            : file.s3File?.key
                    }
                });
                const remappedContent = {
                    ...loadedContent,
                    files: (loadedContent.files || []).map(remapFile),
                    fileGroups: (loadedContent.fileGroups || []).map((g: any) => ({
                        ...g,
                        files: (g.files || []).map(remapFile)
                    }))
                };
                await context.getPageDraftAPI().editContainer(
                    IPageDraft.EditContainerInput.create({
                        newContainerContent: remappedContent,
                        draftRef: draftRef,
                        containerRef: { id: newContainerId, type: EntityType.Container, containerType: Container.Type.DOCUMENT } as ContainerRef,
                    })
                );
                break;
            }
            case Container.Type.DECISION_TREE: {
                // Copy each node's S3 content via presigned download/upload
                const nodes = loadedContent.allNodes || loadedContent.nodes || [];
                for (const node of nodes) {
                    try {
                        const sourceKey = S3File.AskLegalFileKey.toAskLegalFileKeyFromDecisionTreeNode(sourceContainerId, node.id);
                        const getUrlRes = await context.getFileAPI().getPresignedUrl(
                            IFile.PresignedUrlInput.create({
                                key: sourceKey,
                                operation: IFile.PresignedOperation.Get,
                                versionId: node.contentFileVersion,
                                contentType: S3File.ContentType.DecisionTreeNodeContent
                            })
                        );
                        const getUrlOutput = APIOutput.fromRaw<IFile.PresignedUrlOutput>(getUrlRes.data);
                        if (getUrlOutput.isErr()) continue;
                        const downloadRes = await fetch(getUrlOutput.data.presignedUrl);
                        if (!downloadRes.ok) continue;
                        const blob = await downloadRes.blob();
                        const newKey = S3File.AskLegalFileKey.toAskLegalFileKeyFromDecisionTreeNode(newContainerId, node.id);
                        const putUrlRes = await context.getFileAPI().getPresignedUrl(
                            IFile.PresignedUrlInput.create({
                                key: newKey,
                                operation: IFile.PresignedOperation.Put,
                                contentType: S3File.ContentType.DecisionTreeNodeContent
                            })
                        );
                        const putUrlOutput = APIOutput.fromRaw<IFile.PresignedUrlOutput>(putUrlRes.data);
                        if (putUrlOutput.isErr()) continue;
                        await context.getFileAPI().uploadViaPresignedUrl({ data: blob, presignedUrl: putUrlOutput.data.presignedUrl });
                    } catch {
                        // Non-blocking — continue copying remaining nodes
                    }
                }
                // Remap containerId in all nodes to point to the new container
                const remappedContent = DecisionTreeContent.copy(loadedContent as DecisionTreeContent, newContainerId);
                await context.getPageDraftAPI().editContainer(
                    IPageDraft.EditContainerInput.create({
                        newContainerContent: remappedContent,
                        draftRef: draftRef,
                        containerRef: { id: newContainerId, type: EntityType.Container, containerType: Container.Type.DECISION_TREE } as ContainerRef,
                    })
                );
                break;
            }
            case Container.Type.WHOS_MY_LAWYER: {
                // Copy legal contacts
                const loadContactsRes = await context.getLegalContactAPI().loadAllByContainer(
                    ILegalContact.LoadAllLegalContactsByContainerInput.create({
                        containerRef: { id: sourceContainerId, type: EntityType.Container } as EntityRef,
                        pagination: { currentPageIndex: 1, pageSize: 500 },
                        sorting: { field: "legalContactUser", order: "ASC" }
                    })
                );
                const loadContactsOutput = APIOutput.fromRaw<ServerSidePaginatedLoadingOutput<LegalContact.Record>>(loadContactsRes.data);
                if (loadContactsOutput.isErr()) break;
                const contacts = loadContactsOutput.data.result || [];
                for (const contact of contacts) {
                    try {
                        await context.getLegalContactAPI().create(
                            ILegalContact.CreateLegalContactInput.create({
                                containerRef: { id: newContainerId, type: EntityType.Container } as EntityRef,
                                legalContactUser: contact.legalContactUser,
                                businessLeader: contact.businessLeader || [],
                                legalScopeOptions: contact.legalScopeOptions,
                                legalTeamOptions: contact.legalTeamOptions,
                                legalSubTeamOptions: contact.legalSubTeamOptions,
                                additionalFields: contact.additionalFields,
                            })
                        );
                    } catch {
                        // Non-blocking — continue copying remaining contacts
                    }
                }
                break;
            }
            case Container.Type.ADVANCED_LIST: {
                // Create new ALTAR entity and copy items
                const alContent = loadedContent as AdvancedListContent;
                const createRes = await context.getAdvancedListAPI().createAdvancedList({
                    containerId: newContainerId,
                    name: alContent.updateAdvancedListPayload?.name || "",
                    description: alContent.updateAdvancedListPayload?.description || "",
                    addFieldDefinitionsList: alContent.updateAdvancedListPayload?.addFieldDefinitionsList,
                    addEditorsList: alContent.addEditorsList,
                });
                const createOutput = APIOutput.fromRaw<IAdvancedList.CreateAdvancedListOutput>(createRes.data);
                if (createOutput.isErr()) {
                    throw new Error("Failed to create Advanced List entity — some items may not have been copied.");
                }

                const { entityId: newEntityId, repositoryId: newRepoId } = createOutput.data;

                // Copy items from source entity
                const sourceRepoId = alContent.entityRef?.repositoryRef?.repositoryId;
                const sourceEntityId = alContent.entityRef?.entityId;
                if (sourceRepoId && sourceEntityId) {
                    let pageIndex = 1;
                    let hasMore = true;
                    while (hasMore) {
                        const searchRes = await context.getAdvancedListAPI().searchItems({
                            containerId: sourceContainerId,
                            listEntityId: sourceEntityId,
                            listRepositoryId: sourceRepoId,
                            by: SDSUtils.getAmazonPersonRef(context.userId),
                            pagination: { pageIndex, pageSize: 50 }
                        });
                        const searchOutput = APIOutput.fromRaw<IAdvancedList.SearchAdvancedListItemsOutput>(searchRes.data);
                        if (searchOutput.isErr()) break;
                        const items = searchOutput.data.items || [];
                        for (const item of items) {
                            try {
                                await context.getAdvancedListAPI().createItem({
                                    containerId: newContainerId,
                                    entityId: newEntityId,
                                    repositoryId: newRepoId,
                                    by: SDSUtils.getAmazonPersonRef(context.userId),
                                    values: item.values,
                                });
                            } catch { /* non-blocking */ }
                        }
                        hasMore = items.length === 50;
                        pageIndex++;
                    }
                }

                // Update container content to point to new ALTAR entity via editContainer
                const newEntityRef = {
                    entityId: newEntityId,
                    entityTypeRef: { entityTypeId: "ADVANCED_LIST" },
                    repositoryRef: { repositoryId: newRepoId }
                };
                const updatedAlContent = { ...alContent, entityRef: newEntityRef };
                await context.getPageDraftAPI().editContainer(
                    IPageDraft.EditContainerInput.create({
                        newContainerContent: updatedAlContent,
                        draftRef: draftRef,
                        containerRef: { id: newContainerId, type: EntityType.Container, containerType: Container.Type.ADVANCED_LIST } as ContainerRef,
                    })
                );
                break;
            }
            default:
                // Simple types (RichText, Accordion, List) — no post-copy needed
                break;
        }
    };

    const handleSubmit = async () => {
        if (!selectedPage) return;
        setSubmitting(true);
        setError(null);

        try {
            const targetPageId = selectedPage;

            // Step 1: Load the source container's content and metadata
            const loadRes = await context.getContainerAPI().load(props.sourceContainerId);
            const loadOutput = APIOutput.fromRaw<IContainer.LoadContainerOutput>(loadRes.data);
            if (loadOutput.isErr()) {
                setError(`Failed to load source container: ${loadOutput.err.message}`);
                setSubmitting(false);
                return;
            }
            const { loadedContent, containerMetadata } = loadOutput.data;

            // Block unsupported types
            // (all types now supported)

            // Step 2: Load or create draft for the target page
            const loadDraftRes = await context.getPageDraftAPI().loadAllByPageId(targetPageId);
            const loadDraftOutput = APIOutput.fromRaw<PageDraft.Data[]>(loadDraftRes.data);
            if (loadDraftOutput.isErr()) {
                setError(`Failed to load drafts: ${loadDraftOutput.err.message}`);
                setSubmitting(false);
                return;
            }

            let draft: PageDraft.Data | undefined;
            draft = loadDraftOutput.data.find(d => d.status === "DRAFT");

            if (!draft) {
                const createDraftRes = await context.getPageDraftAPI().create(
                    IPageDraft.CreatePageDraftInput.create(
                        { id: targetPageId, type: EntityType.Page } as EntityRef
                    )
                );
                const createDraftOutput = APIOutput.fromRaw<PageDraft.Data>(createDraftRes.data);
                if (createDraftOutput.isErr()) {
                    setError(`Failed to create draft: ${createDraftOutput.err.message}`);
                    setSubmitting(false);
                    return;
                }
                draft = createDraftOutput.data;
            }

            // Step 3: Add container to draft using source content
            // For Decision Tree, pre-remap content (strip contentFileVersion, update containerId placeholder)
            let contentForCreate = loadedContent;
            if (containerMetadata.containerType === Container.Type.DECISION_TREE) {
                // Strip contentFileVersion from nodes so backend loads latest S3 version
                const dtContent = loadedContent as DecisionTreeContent;
                const nodesArray = dtContent.allNodes || (dtContent as any).nodes || [];
                contentForCreate = {
                    ...dtContent,
                    allNodes: nodesArray.map((node: any) => ({
                        ...node,
                        contentFileVersion: undefined
                    }))
                };
            }

            const addRes = await context.getPageDraftAPI().addContainer(
                IPageDraft.AddContainerInput.create({
                    containerContent: contentForCreate,
                    contentType: containerMetadata.containerType,
                    draftRef: { id: draft.id, type: EntityType.PageDraft } as EntityRef,
                    location: props.sourceSection,
                    containerTitle: containerMetadata.title,
                    containerDescription: containerMetadata.description || "",
                    headerDescription: containerMetadata.headerDescription,
                    footerDescription: containerMetadata.footerDescription,
                })
            );
            const addOutput = APIOutput.fromRaw<PageDraft.Data>(addRes.data);
            if (addOutput.isErr()) {
                setError(`Failed to copy container: ${addOutput.err.message}`);
                setSubmitting(false);
                return;
            }

            // Success — set redirect
            const targetSection = addOutput.data.newSections.find(s => s.location === props.sourceSection);
            const newContainerId = targetSection?.containers[targetSection.containers.length - 1]?.id;

            // Step 4: Post-copy resource duplication for complex types
            if (newContainerId) {
                try {
                    await copyTypeSpecificResources(
                        containerMetadata.containerType,
                        props.sourceContainerId,
                        newContainerId,
                        loadedContent,
                        { id: draft!.id, type: EntityType.PageDraft } as EntityRef
                    );
                } catch (e: any) {
                    // Container was created but some resources failed to copy
                    setError(`Container copied, but some items failed to copy: ${e?.message || "unknown error"}`);
                    setSubmitting(false);
                    return;
                }
            }

            if (newContainerId) {
                const containerUrl = window.location.origin + AppURLFactory.getContainer(newContainerId, { type: "draft" });
                setRedirectUrl(containerUrl);
            }
            setSucceeded(true);
        } catch (e: any) {
            setError(e?.message || "Unknown error");
        }
        setSubmitting(false);
    };

    const handleDismiss = () => {
        if (succeeded && redirectUrl && !error) return;
        setSelectedPL(null);
        setSelectedPage(null);
        setError(null);
        setSucceeded(false);
        setSubmitting(false);
        setRedirectUrl(null);
        setRedirectCountdown(5);
        props.onDismiss();
    };

    return (
        <Modal
            visible={props.visible}
            size="medium"
            header="Copy Container to Page"
            onDismiss={submitting || (succeeded && !!redirectUrl) ? () => {} : handleDismiss}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" disabled={submitting || succeeded} onClick={handleDismiss}>Cancel</Button>
                        <Button
                            variant="primary"
                            loading={submitting}
                            disabled={!selectedPage || submitting || succeeded}
                            onClick={handleSubmit}
                        >
                            Copy
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                {submitting && (
                    <Flashbar items={[{
                        type: "in-progress",
                        content: "Copying container — please do not close this tab.",
                        dismissible: false,
                        id: "in-progress"
                    }]} />
                )}
                {succeeded && (
                    <Flashbar items={[{
                        type: "success",
                        content: <span>
                            Container copied successfully.
                            {redirectUrl && <> You will be redirected in {redirectCountdown} seconds.{" "}
                                <Link href={redirectUrl}>View in draft</Link>
                            </>}
                        </span>,
                        dismissible: false,
                        id: "success"
                    }]} />
                )}
                {error && (
                    <Flashbar items={[{
                        type: "error",
                        content: error,
                        dismissible: true,
                        onDismiss: () => setError(null),
                        id: "error"
                    }]} />
                )}
                <UIField.CustomField
                    name="Page Library:"
                    child={<Select
                        selectedOption={pageLibraries.find(o => o.value === selectedPL) || null}
                        options={pageLibraries}
                        onChange={({ detail }) => setSelectedPL(detail.selectedOption.value!)}
                        statusType={pageLibrariesLoaded ? "finished" : "loading"}
                        loadingText="Loading page libraries..."
                        filteringType="auto"
                        placeholder="Select page library"
                        disabled={succeeded}
                    />}
                />
                <UIField.CustomField
                    name="Page:"
                    child={<Select
                        selectedOption={pages.find(o => o.value === selectedPage) || null}
                        options={pages}
                        onChange={({ detail }) => setSelectedPage(detail.selectedOption.value!)}
                        statusType={!selectedPL ? "finished" : pagesLoaded ? "finished" : "loading"}
                        loadingText="Loading pages..."
                        filteringType="auto"
                        placeholder={selectedPL ? "Select page" : "Select a page library first"}
                        disabled={!selectedPL || succeeded}
                    />}
                />
                <Box color="text-body-secondary" fontSize="body-s">
                    The copied container will be added to the bottom of the container list on the destination page.
                </Box>
            </SpaceBetween>
        </Modal>
    );
};
